// @TODO: refactor/redesign this
/**
 * Ryan O'Dowd
 * 2018-03-25
 * © Copyright 2018 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Linking,
  Platform,
  View,
} from 'react-native';
import {
  NOTIFICATIONS_ROUTE,
  apiDeleteAccount,
  apiUpdateUser,
  fetchI18n,
  logout,
  setLightDarkMode,
  setPreferredScale,
  setSelectedTheme,
  setTheme,
  updateUserSettings,
} from '@utils/store/actions';
import React, {
  useState,
} from 'react';
import Button from '@components/common/Button';
import ButtonGroup from '@components/common/ButtonGroup';
import Dialog from '@components/common/Dialog';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import Menu from '@components/common/Menu';
import ScreenWrapper from '@containers/ScreenWrapper';
import SettingCard from './SettingCard';
import Switch from '@components/common/Switch';
import Text from '@components/common/Text';
import TranslationSelectionDialog from '@components/TranslationSelectionDialog';
import getHeaderStyles from '@components/Header/styles';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import {
  themes,
} from '@utils/hooks/useTheme'; // @TODO: shouldn't need to import this
import utilities from '@utils/utilities';

const Settings = () => {
  const dispatch = hooks.useTypedDispatch();
  const headerStyles = hooks.useStyles(getHeaderStyles);
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  hooks.useBackButton(() => {
    navigation.goBack();
    return true;
  });

  const [_isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const [_isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const [_isTranslationSelectionDialogOpen, setIsTranslationSelectionDialogOpen] = useState(false);

  const userSettings = hooks.useTypedSelector((state) => state.userSettings);
  const betaOn = hooks.useTypedSelector((state) => state.userSettings.beta_on);
  const currentTheme = hooks.useTypedSelector((state) => state.theme);
  const locales = hooks.useTypedSelector((state) => state.locales);

  const languageIsLoading = hooks.useTypedSelector((state: any) => state.loadingStates.fetchI18n);

  const isAdmin = hooks.useIsAdmin();

  // @TODO: const user = hooks.useTypedSelector((state) => state.oakUser);
  const preferredTranslation = hooks.usePreferredTranslation();

  const menuItem = (
    <Menu
      statusBarHeight={styles.safeAreaInsets.top}// @TODO: abstract menu to oak component and abstract this prop away
      anchor={
        <Header.Icon
          icon='dots-vertical'
          onPress={() => setIsOverflowMenuOpen(true)}
        />
      }
      visible={_isOverflowMenuOpen}
      onDismiss={() => setIsOverflowMenuOpen(false)}
    >
      <Menu.Item
        title={i18n('deleteAccount')}
        textColor={styles.colors.alert}
        onPress={() => {
          setIsOverflowMenuOpen(false);
          setIsConfirmDialogOpen(true);
        }}
      />
      <Menu.Item
        title={i18n('signOut')}
        onPress={() => {
          dispatch(logout());
          navigation.goBack();
        }}
      />
    </Menu>
  );

  const backIcon = <Header.BackIcon />;

  return (
    <ScreenWrapper>
      <Header
        leftItems={[backIcon]}
        // @TODO: rightItems={!user.auth_provider_info === 'anonymous' ? [menuItem] : [{}]}
        rightItems={[menuItem]}
        centerComponent={betaOn
          ? (
            <View style={styles.centerComponentWrapper}>
              <View style={styles.centerComponentInner}>
                <Text style={[headerStyles.titleText, styles.betaTitleText]}>{i18n('settings')}</Text>
                <Text style={styles.betaSuperscript}> beta</Text>
              </View>
            </View>
          )
          : null
        }
        title={betaOn ? null : i18n('settings')}
        onTitleLongPress={() => {
          if (isAdmin) {
            dispatch(updateUserSettings({beta_on: +(!betaOn)}));
          }
        }}
      />
      <FullScreenScrollView contentContainerStyle={styles.settingsListContainer}>
        <SettingCard
          key='notifications'
          title={i18n('notificationPreferencesTitle')}
          subtitle={i18n('notificationPreferencesSubtitle')}
          onPress={() => navigation.navigate(NOTIFICATIONS_ROUTE)}
        />

        <SettingCard
          key='lightDarkMode'
          title={i18n('darkModeTitle')}
          subtitle={i18n('darkModeSubtitle')}
          actionItem={
            <Switch
              value={styles.isDarkMode}
              onValueChange={() => {
                const newMode = styles.isDarkMode ? 'light' : 'dark';
                dispatch(setLightDarkMode(newMode));
              }}
            />
          }
        />

        {!!betaOn &&
          <SettingCard
            key='themeColor'
            title={i18n('theme')}// @TODO: better description
            subtitle='Change app colors'// @TODO: i18n
            bottomComponent={
              <View>
                <View style={styles.buttonGroupWrapper}>
                  <ButtonGroup
                    options={Object.entries(themes).filter(([__themeName, themeAttributes]: [string, any]) => !themeAttributes.isHidden).map(([themeName, __themeAttributes]) => {
                      return {
                        title: utilities.capitalizeFirstLetter(themeName),
                        value: themeName,
                      };
                    })}
                    isVertical={true}
                    selectedValue={currentTheme}
                    onSelect={(newValue) => {
                      dispatch(setTheme(newValue));
                      dispatch(setSelectedTheme(newValue));
                    }}
                  />
                </View>
              </View>
            }
          />
        }

        <SettingCard
          key='rateUs'
          title={i18n('loveWrinklyTitle')}
          subtitle={i18n('loveWrinklySubtitle', Platform.OS === 'ios' ? 'App Store' : 'Play Store')}
          onPress={() => Linking.openURL(Platform.OS === 'ios' ? globals.appStoreUrl : globals.playStoreUrl)}
        />

        <SettingCard
          key='scale'
          title={i18n('preferredTextSize')}
          subtitle={i18n('preferredTextSizeDescription')}
          bottomComponent={
            <View>
              <View style={styles.buttonGroupWrapper}>
                <ButtonGroup
                  options={['small', 'medium', 'large', 'extraLarge'].map((s) => {
                    let scaleFactor = globals.fontScales.medium;
                    if (s === 'small') {
                      scaleFactor = globals.fontScales.small;
                    } else if (s === 'large') {
                      scaleFactor = globals.fontScales.large;
                    } else if (s === 'extraLarge') {
                      scaleFactor = globals.fontScales.extraLarge;
                    }

                    return {
                      title: i18n(s),
                      value: s,
                      textStyle: {
                        fontSize: 16 * scaleFactor,
                      },
                    };
                  })}
                  selectedValue={Object.entries(globals.fontScales).find(([__k, v]) => v === styles.globalScale)?.[0]}
                  onSelect={(newValue) => dispatch(setPreferredScale(newValue))}
                  isVertical={true}
                />
              </View>
            </View>
          }
        />

        <SettingCard
          key='version'
          title={i18n('preferredTranslation')}
          subtitle={i18n('preferredTranslationBody')}
          bottomComponent={<View style={styles.currentTranslationWrapper}><Text style={styles.currentTranslation}>{preferredTranslation.name} ({preferredTranslation.abbreviation})</Text></View>}
          onPress={() => setIsTranslationSelectionDialogOpen(true)}
        />

        {!!(betaOn || userSettings.locale_id === 2) &&
          <SettingCard
            key='language'
            title={i18n('languageTitle')}
            subtitle={i18n('languageSubtitle')}
            bottomComponent={
              <View>
                <View style={styles.buttonGroupWrapper}>
                  <ButtonGroup
                    options={Object.values(locales).map((l: any) => {
                      return {
                        title: l.language_name_local,
                        value: l.id,
                      };
                    })}
                    disabled={languageIsLoading}
                    isVertical={true}
                    selectedValue={userSettings.locale_id}
                    onSelect={(newValue) => {
                      dispatch(apiUpdateUser({locale_id: newValue}));
                      dispatch(fetchI18n(newValue, false, true));
                    }}
                  />
                </View>
              </View>
            }
          />
        }
      </FullScreenScrollView>
      <TranslationSelectionDialog
        isOpen={_isTranslationSelectionDialogOpen}
        onClose={() => setIsTranslationSelectionDialogOpen(false)}
        autoClose={false}
        closeButtonText={i18n('close')}
      />
      {_isConfirmDialogOpen &&
        <Dialog
          visible={_isConfirmDialogOpen}
          onDismiss={() => setIsConfirmDialogOpen(false)}
        >
          <Dialog.Title>{i18n('deleteAccountTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text>{i18n('deleteAccountBody')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              onPress={() => setIsConfirmDialogOpen(false)}
            >
              {i18n('deleteAccountCancel')}
            </Button>
            <Button
              mode='contained'
              theme={styles.colors.alert}
              onPress={() => {
                dispatch(apiDeleteAccount());
                setIsConfirmDialogOpen(false);
                navigation.goBack();
              }}
            >
              {i18n('deleteAccountConfirm')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
    </ScreenWrapper>
  );
};

export default Settings;
